import React, { useState, useEffect, useRef, useCallback } from "react";
import { useStats } from "./StatsContext";
import styles from "./TlSelector.module.css";

const DEBOUNCE_MS = 300;

const TlSelector = () => {
  const {
    tlList,
    tlListMeta,
    tlSearchQuery,
    setTlSearchQuery,
    loadTlList,
    selectedTlToken,
  } = useStats();

  const [isOpen, setIsOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState(tlSearchQuery);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Load first page on mount
  useEffect(() => {
    loadTlList(1, "");
  }, [loadTlList]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  // Debounced search
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalSearch(value);

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setTlSearchQuery(value);
        loadTlList(1, value);
      }, DEBOUNCE_MS);
    },
    [setTlSearchQuery, loadTlList]
  );

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 50;

    if (nearBottom && tlListMeta.hasNextPage && !tlListMeta.isLoading) {
      loadTlList(tlListMeta.currentPage + 1, localSearch);
    }
  }, [tlListMeta, loadTlList, localSearch]);

  // Select a LaserLINK — navigate to its stats page
  const handleSelect = useCallback(
    (tl) => {
      setIsOpen(false);
      if (tl.token !== selectedTlToken) {
        window.location.href = `/tl-stats/${tl.token}#/at-a-glance`;
      }
    },
    [selectedTlToken]
  );

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen]);

  // Determine display value for trigger
  const currentTl = tlList.find((t) => t.token === selectedTlToken);
  const { tl } = window?.objInit || {};
  const displayName = currentTl?.name || tl?.name || "Select a LaserLINK";
  const displayFlag = currentTl?.language_flag || tl?.language;

  return (
    <div className={styles.selectorWrapper}>
      {/* Trigger button */}
      <div
        className={`${styles.selectorTrigger} ${isOpen ? styles.selectorTriggerOpen : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select a LaserLINK"
      >
        <div className={styles.triggerContent}>
          {displayFlag?.class && (
            <span
              className={displayFlag.class}
              style={{ width: "22px", display: "inline-block" }}
            ></span>
          )}
          <span className={styles.triggerTitle}>{displayName}</span>
          {displayFlag?.name && (
            <span className={styles.triggerLang}>({displayFlag.name})</span>
          )}
        </div>
        <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}>
          &#9660;
        </span>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <>
          <div className={styles.overlay} onClick={() => setIsOpen(false)} />
          <div className={styles.dropdownPanel} role="listbox">
            {/* Search */}
            <div className={styles.searchWrapper}>
              <input
                ref={searchRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search LaserLINX..."
                value={localSearch}
                onChange={handleSearchChange}
                aria-label="Search LaserLINX"
              />
            </div>

            {/* List */}
            <div
              ref={listRef}
              className={styles.listContainer}
              onScroll={handleScroll}
            >
              {/* Initial loading */}
              {tlListMeta.isLoading && tlList.length === 0 && (
                <div className={styles.loadingRow}>
                  <div className={styles.spinner}></div>
                  <span>Loading LaserLINX...</span>
                </div>
              )}

              {/* Empty state */}
              {!tlListMeta.isLoading && tlList.length === 0 && (
                <div className={styles.emptyState}>
                  {localSearch
                    ? "No matching LaserLINX found"
                    : "No LaserLINX with statistics found"}
                </div>
              )}

              {/* TL items */}
              {tlList.map((tlItem) => (
                <div
                  key={tlItem.token}
                  className={`${styles.tlItem} ${
                    tlItem.token === selectedTlToken ? styles.tlItemActive : ""
                  }`}
                  onClick={() => handleSelect(tlItem)}
                  role="option"
                  aria-selected={tlItem.token === selectedTlToken}
                >
                  {tlItem.language_flag?.class && (
                    <span
                      className={`${tlItem.language_flag.class} ${styles.tlItemFlag}`}
                    ></span>
                  )}
                  <div className={styles.tlItemInfo}>
                    <div className={styles.tlItemTitle}>{tlItem.name}</div>
                    <div className={styles.tlItemMeta}>
                      {tlItem.language_flag?.name || tlItem.language}
                      {tlItem.domain && ` \u00B7 ${tlItem.domain}`}
                    </div>
                  </div>
                  <div className={styles.tlItemVisits}>
                    {tlItem.total_counts} visits
                  </div>
                </div>
              ))}

              {/* Loading more (infinite scroll) */}
              {tlListMeta.isLoading && tlList.length > 0 && (
                <div className={styles.loadingRow}>
                  <div className={styles.spinner}></div>
                  <span>Loading more...</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TlSelector;
